import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Paper,
  Grid,
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Divider,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { format } from 'date-fns';
import { uk } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_API_URL;

const formatNumber = (number) => {
  if (number === null || number === undefined || Number.isNaN(Number(number)))
    return '0';
  return Number(number).toLocaleString('uk-UA', { maximumFractionDigits: 2 });
};

const getServiceFee = (event) => Number(event?.report?.serviceFee) || 0;
const getTicketsSold = (event) => Number(event?.report?.tickets_sell) || 0;
const getRevenue = (event) => Number(event?.report?.price) || 0;

const monthKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

const emptyMonth = () => ({
  eventsCount: 0,
  eventsWithSales: 0,
  ticketsSold: 0,
  revenue: 0,
  serviceFee: 0,
});

// Строка вида "  🎟 Квитків продано: 372"
const StatRow = ({ icon, label, value }) => (
  <Typography variant="body2" sx={{ pl: 1, py: 0.25 }}>
    {icon} {label}: <strong>{value}</strong>
  </Typography>
);

const ServiceFeeStats = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Сдвиг окна в месяцах: 0 — текущий месяц по центру
  const [monthOffset, setMonthOffset] = useState(0);

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/events-bo`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { limit: 1000 },
        });
        setEvents(response.data.events || []);
      } catch (err) {
        console.error('Помилка при отриманні статистики збору:', err);
        setError('Не вдалося завантажити статистику сервісного збору');
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  // Агрегация всех событий по месяцу проведения
  const byMonth = useMemo(() => {
    const map = new Map();

    events.forEach((event) => {
      if (!event.date) return;
      const key = monthKey(new Date(event.date));
      if (!map.has(key)) map.set(key, emptyMonth());

      const month = map.get(key);
      const fee = getServiceFee(event);
      month.eventsCount += 1;
      if (fee > 0) month.eventsWithSales += 1;
      month.ticketsSold += getTicketsSold(event);
      month.revenue += getRevenue(event);
      month.serviceFee += fee;
    });

    return map;
  }, [events]);

  // Окно из трёх месяцев: попередній — поточний — наступний
  const months = useMemo(() => {
    const now = new Date();
    return [-1, 0, 1].map((shift) => {
      const date = new Date(
        now.getFullYear(),
        now.getMonth() + monthOffset + shift,
        1,
      );
      return {
        key: monthKey(date),
        date,
        isCurrent: shift === 0 && monthOffset === 0,
        ...(byMonth.get(monthKey(date)) || emptyMonth()),
      };
    });
  }, [byMonth, monthOffset]);

  // Итог за всё время — не зависит от выбранного окна
  const allTime = useMemo(
    () =>
      events.reduce(
        (acc, event) => {
          const fee = getServiceFee(event);
          return {
            eventsCount: acc.eventsCount + 1,
            eventsWithSales: acc.eventsWithSales + (fee > 0 ? 1 : 0),
            ticketsSold: acc.ticketsSold + getTicketsSold(event),
            revenue: acc.revenue + getRevenue(event),
            serviceFee: acc.serviceFee + fee,
          };
        },
        emptyMonth(),
      ),
    [events],
  );

  if (loading) {
    return (
      <Paper elevation={3} sx={{ p: 3, mb: 3, textAlign: 'center' }}>
        <CircularProgress />
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography color="error">{error}</Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Typography variant="h6">📊 Статистика сервісного збору</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => setMonthOffset((prev) => prev - 1)}
            title="Попередні місяці"
          >
            <ChevronLeftIcon />
          </IconButton>
          <Button
            size="small"
            onClick={() => setMonthOffset(0)}
            disabled={monthOffset === 0}
          >
            Поточний місяць
          </Button>
          <IconButton
            size="small"
            onClick={() => setMonthOffset((prev) => prev + 1)}
            title="Наступні місяці"
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>

      <Paper
        elevation={1}
        sx={{
          p: 2,
          mb: 3,
          bgcolor: 'success.light',
          color: 'success.contrastText',
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          🏆 За весь час
        </Typography>
        <Typography variant="h4" sx={{ my: 0.5 }}>
          {formatNumber(allTime.serviceFee)} грн
        </Typography>
        <Typography variant="body2">
          🎭 Івентів: {allTime.eventsCount} (продажі: {allTime.eventsWithSales})
          {'  •  '}🎟 Квитків: {formatNumber(allTime.ticketsSold)}
          {'  •  '}💵 Сума продажів: {formatNumber(allTime.revenue)} грн
        </Typography>
      </Paper>

      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={2}>
        {months.map((month) => (
          <Grid item xs={12} md={4} key={month.key}>
            <Paper
              elevation={month.isCurrent ? 3 : 1}
              sx={{
                p: 2,
                height: '100%',
                bgcolor: month.isCurrent ? '#f1fbf7' : 'background.default',
                borderLeft: '4px solid',
                borderColor: month.serviceFee > 0 ? 'success.main' : 'divider',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, textTransform: 'capitalize' }}
                >
                  {format(month.date, 'LLLL yyyy', { locale: uk })}
                </Typography>
                {month.isCurrent && (
                  <Typography variant="caption" color="success.main">
                    поточний
                  </Typography>
                )}
              </Box>
              <StatRow
                icon="🎭"
                label="Івентів"
                value={`${month.eventsCount} (продажі: ${month.eventsWithSales})`}
              />
              <StatRow
                icon="🎟"
                label="Квитків продано"
                value={formatNumber(month.ticketsSold)}
              />
              <StatRow
                icon="💵"
                label="Сума продажів"
                value={`${formatNumber(month.revenue)} грн`}
              />
              <StatRow
                icon="💰"
                label="Сервісний збір"
                value={`${formatNumber(month.serviceFee)} грн`}
              />
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
};

export default ServiceFeeStats;
